import * as React from 'react';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import MenuList from '@mui/material/MenuList';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import { PdfIcon } from 'src/icons/PdfIcon';
import { CloudDownloadIcon } from 'src/icons/CloudDownloadIcon';
import { DescriptionIcon } from 'src/icons/DescriptionIcon';
import { DownloadIcon } from 'src/icons/DownloadIcon';
import { ts } from "src/utils/translate";
import { hasPermissionForFuncInProject } from "src/utils/controllerHasPermissionsAdapter";
import { useGetPaidFeaturesQuery } from 'src/api/v1/feature-ApiSlice';
import Tooltip from '@mui/material/Tooltip';
import { GetQuotePdfRequestModel } from "src/model/api/quote/get-quote-pdf-request.model";
import { EXPORTED_OFFER_DIV_ID } from "src/utils/constants";
import { getOfferExportHtml } from "src/utils/documentationUtils";
import { getAPIURL } from "src/utils/url";
import { useFileDownload } from "src/api/file-download-service";
import { useGetQuoteQuery } from "src/api/v1/quote-ApiSlice";
import { getFileDate } from "src/utils/controllerAdapterForOfferPage";
import { GetBomAsExcelRequest } from "src/model/api/quote/get-bom-as-excel-request.model";
import { LoxoneConfigDownloadRequest } from "src/model/api/quote/loxone-config-download-request.model";
import { ElectricalPlanGenerateRequest } from "src/model/api/quote/electrical-plan-generate-request.model";
import {
  DownloadGateState,
  getExcelDownloadDisabledReason,
  getLoxoneDownloadDisabledReason,
  getOneWireDownloadDisabledReason,
  getPdfDownloadDisabledReason
} from "src/utils/downloadDisabledReason";

export interface QuoteDownloadButtonProps {
  projectId: number;
  isPro: boolean;
}

export function QuoteDownloadButton(props: QuoteDownloadButtonProps) {

  const {data: paidFeaturesResponse} = useGetPaidFeaturesQuery(Number(props.projectId))
  const {data: quote} = useGetQuoteQuery(Number(props.projectId));

  const { fetchData } = useFileDownload();

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };


  const downloadGateState: DownloadGateState = {
    isPro: props.isPro,
    loxoneConfigPaid: !!paidFeaturesResponse?.loxoneconfig_paid,
    stromlaufplanPaid: !!paidFeaturesResponse?.stromlaufplan_paid,
    isLoxoneConfigDownloadAvailable: !!paidFeaturesResponse?.is_loxone_config_download_available,
    hasPermission: (functionName) => hasPermissionForFuncInProject(functionName, props.projectId)
  };

  const pdfDownloadDisabled = getPdfDownloadDisabledReason(downloadGateState);
  const excelDownloadDisabled = getExcelDownloadDisabledReason(downloadGateState);
  const loxonDownloadDisabled = getLoxoneDownloadDisabledReason(downloadGateState);
  const oneWireDownloadDisabled = getOneWireDownloadDisabledReason(downloadGateState);

  function exportProjectToPdf() {
    const exportRoot = window?.document?.getElementById(EXPORTED_OFFER_DIV_ID);
    const pageHTML = exportRoot ? getOfferExportHtml(exportRoot) : "";
    const request: GetQuotePdfRequestModel = {
      project_id: props.projectId,
      getHeader: true,
      getFooter: true,
      inner_html: pageHTML
    };
    const date = getFileDate();
    // @ts-ignore
    const fileName = quote["project-base"].project_name + " " + date + " " + ts("offer", "offer") + ".pdf";
    fetchData(request, `${getAPIURL()}/v1/quote/get-quote-pdf`, fileName).then();
  }

  function exportBomAsExcel() {
    const request: GetBomAsExcelRequest = {
      project_id: props.projectId,
    };
    const date = getFileDate();
    // @ts-ignore
    const fileName = quote["project-base"].project_name + " " + date + ".xlsx";
    fetchData(request, `${getAPIURL()}/v1/quote/get-bom-as-excel`, fileName).then();
  }

  function exportLoxoneConfig() {
    const request: LoxoneConfigDownloadRequest = {
      project_id: props.projectId,
    };
    const date = getFileDate();
    // @ts-ignore
    const fileName = quote["project-base"].project_name + " " + date + ".Loxone";
    fetchData(request, `${getAPIURL()}/v1/loxone-config/download`, fileName).then();
  }

  function exportElactricalPlan() {
    const request: ElectricalPlanGenerateRequest = {
      project_id: props.projectId,
    };
    const date = getFileDate();
    // @ts-ignore
    const fileName = quote["project-base"].project_name + " " + date + ".pdf";
    fetchData(request, `${getAPIURL()}/v1/electrical-plan/generate`, fileName).then();
  }

  return (
      <div style={{ display: "flex", justifyContent: "flex-end", paddingRight: "10px" }}>
        <Button
            id="demo-customized-button"
            aria-controls={open ? 'demo-customized-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={open ? 'true' : undefined}
            variant="contained"
            disableElevation
            onClick={handleClick}
            startIcon={<CloudDownloadIcon/>}
        >
          {ts("docu_download", "Download")}
        </Button>
        <Paper sx={{width: 0}}>
          <Menu
              anchorEl={anchorEl}
              open={open}
              onClose={handleClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}>
            <MenuList>
              <Tooltip title={pdfDownloadDisabled.reason}>
                <span>
                  <MenuItem disabled={pdfDownloadDisabled.disabled} onClick={exportProjectToPdf}>
                    <ListItemIcon>
                      <PdfIcon fontSize="small" color="primary"/>
                    </ListItemIcon>
                    <ListItemText>{ts("b_save_pdf", "Offer PDF")}</ListItemText>
                  </MenuItem>
                </span>
              </Tooltip>
              <Tooltip title={excelDownloadDisabled.reason}>
                <span>
                  <MenuItem disabled={excelDownloadDisabled.disabled} onClick={exportBomAsExcel}>
                    <ListItemIcon>
                      <DownloadIcon fontSize="small" color="primary"/>
                    </ListItemIcon>
                    <ListItemText>{ts("b_save_xls", "Bill of Materials XLSX")}</ListItemText>
                  </MenuItem>
                </span>
              </Tooltip>
              <Tooltip title={loxonDownloadDisabled.reason}>
                <span>
                  <MenuItem disabled={loxonDownloadDisabled.disabled} onClick={exportLoxoneConfig}>
                    <ListItemIcon>
                      <CloudDownloadIcon fontSize="small" color="primary"/>
                    </ListItemIcon>
                    <ListItemText>{ts("b_save_loxone", "Loxone Config file")}</ListItemText>
                  </MenuItem>
                </span>
              </Tooltip>
              <Tooltip title={oneWireDownloadDisabled.reason}>
                <span>
                  <MenuItem disabled={oneWireDownloadDisabled.disabled} onClick={exportElactricalPlan}>
                    <ListItemIcon>
                      <DescriptionIcon fontSize="small" color="primary"/>
                    </ListItemIcon>
                    <ListItemText>{ts("b_save_electrical", "Electrical cabinet drawing PDF")}</ListItemText>
                  </MenuItem>
                </span>
              </Tooltip>
            </MenuList>
          </Menu>
        </Paper>
      </div>
  );
}